import React, { useState, useEffect } from "react";
import CustomModal from "../../components/CustomModal";
import { useCreateRelease, useUpdateRelease } from "./hooks";
import { s3API, Release } from "./api";
import { useT } from "../../i18n";

interface NewReleaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editRelease?: Release | null; // Add optional release for editing
}

const releaseTypes = [
  "Major Release",
  "Minor Update",
  "Bug Fix",
  "Security Update",
];

const NewReleaseModal: React.FC<NewReleaseModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editRelease,
}) => {
  const t = useT();
  const [releaseName, setReleaseName] = useState("");
  const [version, setVersion] = useState("");
  const [type, setType] = useState("Major Release");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);

  const createReleaseMutation = useCreateRelease();
  const updateReleaseMutation = useUpdateRelease();

  const isEditMode = !!editRelease;

  // Populate form when editing
  useEffect(() => {
    if (isEditMode && editRelease) {
      setReleaseName(editRelease.releaseName);
      setVersion(editRelease.version);
      setType(editRelease.type);
      setDescription(editRelease.description || "");
      // Note: We don't set the file for editing as it's already uploaded
    } else if (!isEditMode) {
      resetForm();
    }
  }, [editRelease, isEditMode, isOpen]);

  const resetForm = () => {
    setReleaseName("");
    setVersion("");
    setType("Major Release");
    setDescription("");
    setFile(null);
    setError("");
    setUploading(false);
  };

  const handleSave = async () => {
    setError("");

    // Basic validation
    if (!releaseName || !version || !type) {
      setError(t("common.validation.requiredFields"));
      return;
    }

    // Version validation (basic semver check)
    const versionRegex = /^\d+\.\d+\.\d+$/;
    if (!versionRegex.test(version)) {
      setError(t("common.validation.invalidVersion"));
      return;
    }

    try {
      setUploading(file ? true : false);
      let downloadKey, fileSize, fileType;

      // Upload file to S3 if provided (for new files or when replacing existing file)
      if (file) {
        const uploadResponse = await s3API.uploadReleaseFile(
          file,
          releaseName,
          version
        );
        downloadKey = uploadResponse.data.fileKey;
        fileSize = uploadResponse.data.fileSize;
        fileType = uploadResponse.data.fileType;
      } else if (isEditMode && editRelease) {
        // Keep existing file info if no new file is uploaded
        downloadKey = editRelease.downloadKey;
        fileSize = editRelease.fileSize;
        fileType = editRelease.fileType;
      }

      if (isEditMode && editRelease) {
        // Update existing release
        await updateReleaseMutation.mutateAsync({
          id: editRelease.id,
          data: {
            releaseName,
            version,
            type,
            description: description || undefined,
            downloadKey,
            fileSize,
            fileType,
          },
        });
      } else {
        // Create new release
        await createReleaseMutation.mutateAsync({
          releaseName,
          version,
          type,
          description: description || undefined,
          downloadKey,
          fileSize,
          fileType,
        });
      }

      resetForm();
      onClose();
      onSuccess?.();
    } catch (error: any) {
      setError(
        t(isEditMode ? "releases.toast.updateFailed" : "releases.toast.createFailed")
      );
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <CustomModal open={isOpen} onClose={onClose}>
      <div className="w-[720px] max-w-full h-auto flex flex-col mx-auto">
        <div className="flex flex-col w-full">
          <div className="flex items-center gap-3 pt-6 pb-4 border-b border-gray-200 w-full">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-50">
              <i className="fa-solid fa-upload text-2xl text-blue-500"></i>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {isEditMode ? t("releases.modal.editTitle") : t("releases.modal.createTitle")}
              </h2>
              <div className="text-sm text-gray-400 font-medium leading-tight">
                {isEditMode
                  ? t("releases.modal.editSubtitle")
                  : t("releases.modal.createSubtitle")}
              </div>
            </div>
          </div>
          <form
            className="py-6 space-y-4 w-full"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("releases.modal.nameLabel")}
                </label>
                <input
                  type="text"
                  value={releaseName}
                  onChange={(e) => setReleaseName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder={t("releases.modal.namePlaceholder")}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("releases.modal.versionLabel")}
                </label>
                <input
                  type="text"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder={t("releases.modal.versionPlaceholder")}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("releases.modal.typeLabel")}
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                required
              >
                {releaseTypes.map((releaseType) => (
                  <option key={releaseType} value={releaseType}>
                    {releaseType}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("releases.modal.descriptionLabel")}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder={t("releases.modal.descriptionPlaceholder")}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("releases.modal.fileLabel")}{" "}
                {isEditMode && t("releases.modal.fileOptionalHint")}
              </label>
              <div className="relative">
                <input
                  type="file"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  accept=".zip,.exe,.msi,.dmg,.pkg,.tar.gz,.deb,.rpm"
                />
                {file && (
                  <div className="mt-2 text-sm text-gray-600">
                    {t("releases.modal.selectedFile", {
                      name: file.name,
                      size: (file.size / (1024 * 1024)).toFixed(1),
                    })}
                  </div>
                )}
                {isEditMode && editRelease && !file && (
                  <div className="mt-2 text-sm text-gray-500">
                    {t("releases.modal.currentFile")}{" "}
                    {editRelease.downloadKey?.split("/").pop() || t("releases.modal.noFile")}
                    {editRelease.fileSize && ` (${editRelease.fileSize})`}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
              >
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                disabled={
                  createReleaseMutation.isPending ||
                  updateReleaseMutation.isPending ||
                  uploading
                }
                className="px-4 py-2 text-sm font-medium text-white bg-[#111827] rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {uploading ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                    {t("common.loading.uploadingFile")}
                  </>
                ) : createReleaseMutation.isPending ||
                  updateReleaseMutation.isPending ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                    {isEditMode ? t("common.loading.updating") : t("common.loading.creating")}
                  </>
                ) : (
                  <>
                    <i
                      className={`fa-solid ${
                        isEditMode ? "fa-save" : "fa-upload"
                      } mr-2`}
                    ></i>
                    {isEditMode ? t("releases.modal.submitUpdate") : t("releases.modal.submitCreate")}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </CustomModal>
  );
};

export default NewReleaseModal;
